import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Button, Select, TextInput, useToast } from '@ministry/ui/internal';

import { createUser, type CreateUserInput } from '../../lib/admin-users.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { genericFieldErrorMessage } from '../../lib/field-errors.js';
import { checkPasswordClientSide, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../lib/password-policy.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { OrgUnitPicker } from './OrgUnitPicker.js';
import { ADMIN_ROUTES } from './routes.js';

/**
 * SCREEN_SPECS.md I19 (create half) — `POST /users`. `designationId` is deliberately not
 * offered: it is optional on the server schema and no `/reference/designations` endpoint
 * exists on this build to populate a picker (API_CONTRACT §13.9 lists no such route) —
 * an account created here simply carries no designation until one becomes assignable,
 * rather than this form inventing a text field the server would treat as an opaque id.
 * A role is granted separately, from the user's own detail page (I19's "role assignments
 * in context") — creating an account and granting it a role are two different privileged
 * writes and this form does not conflate them.
 */
export function UserCreatePage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { push } = useToast();

  const [username, setUsername] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [locale, setLocale] = React.useState<'ne' | 'en'>('ne');
  const [orgUnitId, setOrgUnitId] = React.useState<string | undefined>(undefined);
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  const mutation = useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: (created) => {
      push({ variant: 'success', title: t('admin.userCreate.toast.success', { name: created.fullName }) });
      navigate(ADMIN_ROUTES.userDetail(created.id), { replace: true });
    },
  });

  const usernameError =
    touched.username && !/^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/.test(username.trim())
      ? t('admin.userCreate.username.error')
      : undefined;
  const fullNameError = touched.fullName && fullName.trim().length === 0 ? t('admin.userCreate.fullName.error') : undefined;
  /**
   * The POLICY, not merely "not empty". This form previously checked `length === 0`
   * while `lib/password-policy.ts` — already used by Account Settings and Password Reset
   * — stated the real rule, so an 8-character password submitted cleanly and came back
   * as a server `422` the form could not attach to any field. Same module, same rules,
   * same live checklist as the other two screens (CLAUDE.md §5, reuse before writing).
   */
  const passwordCheck = checkPasswordClientSide(password, username.trim() || undefined);
  const passwordError =
    touched.password && !passwordCheck.acceptable
      ? t(`admin.userCreate.password.rejected.${passwordCheck.rejections[0] ?? 'too_short'}`, {
          min: PASSWORD_MIN_LENGTH,
          max: PASSWORD_MAX_LENGTH,
        })
      : undefined;

  /**
   * R-055: required, and enforced here as well as at the schema, so the operator is told
   * before they submit rather than after. The field was optional and unmarked, and an
   * account created without it was immediately unreachable by its own creator.
   */
  const orgUnitError = touched.primaryOrgUnitId && orgUnitId === undefined ? t('admin.userCreate.orgUnit.error') : undefined;

  const fieldErrorFor = (field: string): string | undefined => {
    if (!(mutation.error instanceof ApiError)) return undefined;
    const match = mutation.error.fieldErrors.find((fe) => fe.field === field);
    if (!match) return undefined;
    return genericFieldErrorMessage(t, match.code, match.params);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ username: true, fullName: true, password: true, primaryOrgUnitId: true });
    if (usernameError || fullNameError || passwordError || orgUnitId === undefined) return;
    mutation.mutate({
      username: username.trim(),
      fullName: fullName.trim(),
      password,
      email: email.trim() === '' ? undefined : email.trim(),
      phone: phone.trim() === '' ? undefined : phone.trim(),
      locale,
      primaryOrgUnitId: orgUnitId,
    });
  };

  const RENDERED_FIELDS = new Set(['username', 'fullName', 'password', 'email', 'phone', 'primaryOrgUnitId']);
  const bannerError = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) {
      return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    }
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === 'CONFLICT_DUPLICATE') {
        return <AlertBanner variant="critical" title={t('admin.userCreate.error.duplicateUsername')} />;
      }
      const unrenderable = mutation.error.fieldErrors.filter((fe) => !RENDERED_FIELDS.has(fe.field));
      if (unrenderable.length > 0) {
        return (
          <AlertBanner
            variant="critical"
            title={unrenderable.map((fe) => `${fe.field}: ${genericFieldErrorMessage(t, fe.code, fe.params)}`).join(' ')}
          />
        );
      }
      if (mutation.error.fieldErrors.length > 0) {
        return <AlertBanner variant="critical" title={t('admin.error.checkFields')} />;
      }
      return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
    }
    return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
  })();

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <PageHeader
        title={t('admin.userCreate.title')}
        description={t('admin.userCreate.description')}
        breadcrumb={[
          { label: t('admin.users.title'), href: ADMIN_ROUTES.users },
          { label: t('admin.userCreate.title'), href: ADMIN_ROUTES.userNew },
        ]}
      />

      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        {bannerError}
        <TextInput
          id="admin-user-create-username"
          label={t('admin.userCreate.username.label')}
          required
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, username: true }))}
          errorText={usernameError ?? fieldErrorFor('username')}
          helperText={t('admin.userCreate.username.hint')}
        />
        <TextInput
          id="admin-user-create-fullname"
          label={t('admin.userCreate.fullName.label')}
          required
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, fullName: true }))}
          errorText={fullNameError ?? fieldErrorFor('fullName')}
        />
        <TextInput
          id="admin-user-create-password"
          label={t('admin.userCreate.password.label')}
          required
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
          errorText={passwordError ?? fieldErrorFor('password')}
          helperText={t('admin.userCreate.password.hint', { min: PASSWORD_MIN_LENGTH })}
        />
        <TextInput
          id="admin-user-create-email"
          label={t('admin.userCreate.email.label')}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          errorText={fieldErrorFor('email')}
        />
        <TextInput
          id="admin-user-create-phone"
          label={t('admin.userCreate.phone.label')}
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          errorText={fieldErrorFor('phone')}
        />
        <Select
          id="admin-user-create-locale"
          label={t('admin.userCreate.locale.label')}
          value={locale}
          onValueChange={(value) => setLocale(value as 'ne' | 'en')}
          options={[
            { value: 'ne', label: t('admin.userCreate.locale.ne') },
            { value: 'en', label: t('admin.userCreate.locale.en') },
          ]}
        />
        <OrgUnitPicker
          id="admin-user-create-orgunit"
          label={t('admin.userCreate.orgUnit.label')}
          value={orgUnitId}
          onValueChange={setOrgUnitId}
          errorText={orgUnitError ?? fieldErrorFor('primaryOrgUnitId')}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={() => navigate(ADMIN_ROUTES.users)}>
            {t('admin.common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? t('admin.userCreate.submitting') : t('admin.userCreate.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
}
